import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { theme } from './theme';
import { useSalonContextStore } from './store/salonContextStore';
import { ConfirmProvider } from './components/ui/ConfirmDialog';

// Layout & guards
import { DashboardLayout } from './components/DashboardLayout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Public pages
import { HomePage } from './pages/HomePage';
import { PublicSalonPage } from './pages/PublicSalonPage';
import { PublicBookingPage } from './pages/PublicBookingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';

// Shared pages
import { DashboardPage } from './pages/DashboardPage';
import { SalonsPage } from './pages/SalonsPage';
import { CreateSalonPage } from './pages/CreateSalonPage';
import { SalonDetailPage } from './pages/SalonDetailPage';
import { BookingPage } from './pages/BookingPage';
import { MyBookingsPage } from './pages/MyBookingsPage';

// Super Admin pages
import { SuperAdminSalonsPage } from './pages/admin/SuperAdminSalonsPage';
import { SuperAdminStatsPage } from './pages/admin/SuperAdminStatsPage';

// Salon Owner pages
import { SalonSettingsPage } from './pages/salon/SalonSettingsPage';
import { SalonServicesPage } from './pages/salon/SalonServicesPage';
import { SalonStaffPage } from './pages/salon/SalonStaffPage';
import { StaffProfilePage } from './pages/salon/StaffProfilePage';
import { SalonCalendarPage } from './pages/salon/SalonCalendarPage';
import { SalonClientsPage } from './pages/salon/SalonClientsPage';
import { SalonInventoryPage } from './pages/salon/SalonInventoryPage';
import { SalonStatsPage } from './pages/salon/SalonStatsPage';
import { SalonSalesPage } from './pages/salon/SalonSalesPage';
import { SalonMarketingPage } from './pages/salon/SalonMarketingPage';
import { SalonGiftsPage } from './pages/salon/SalonGiftsPage';
import { SalonProfilePage } from './pages/salon/SalonProfilePage';
import { SalonWebsitePage } from './pages/salon/SalonWebsitePage';
import { SalonAccountPage } from './pages/salon/SalonAccountPage';
import { SalonUsersPage } from './pages/salon/SalonUsersPage';
import { SalonSubscriptionPage } from './pages/salon/SalonSubscriptionPage';
import { SalonPaymentsPage } from './pages/salon/SalonPaymentsPage';
import BrandsPage from './pages/salon/BrandsPage';
import ProductsPage from './pages/salon/ProductsPage';
import InventoryAuditPage from './pages/salon/InventoryAuditPage';
import StockTransfersPage from './pages/salon/StockTransfersPage';

// Client pages
import { ClientProfilePage } from './pages/client/ClientProfilePage';

// Employee module — employee self-service
import EmployeeTimePanel from './pages/employee/EmployeeTimePanel';
import MyAbsencesPage from './pages/employee/MyAbsencesPage';
import MyPayrollPage from './pages/employee/MyPayrollPage';
// Employee module — admin / manager
import TimeDashboardPage from './pages/time/TimeDashboardPage';
import TimeCalendarPage from './pages/time/TimeCalendarPage';
import TodayAttendancePage from './pages/time/TodayAttendancePage';
import AdjustmentsPage from './pages/time/AdjustmentsPage';
import AbsencesAdminPage from './pages/time/AbsencesAdminPage';
import SettlementSettingsPage from './pages/time/SettlementSettingsPage';
import PayrollPeriodsPage from './pages/payroll/PayrollPeriodsPage';
import SimulatorPage from './pages/payroll/SimulatorPage';
import PayrollRulesPage from './pages/payroll/PayrollRulesPage';
import EmployeesPage from './pages/payroll/EmployeesPage';
import ProfitabilityPage from './pages/profitability/ProfitabilityPage';
// Tasks module
import TasksPage from './pages/tasks/TasksPage';
import TaskDetailPage from './pages/tasks/TaskDetailPage';
import TaskStatsPage from './pages/tasks/TaskStatsPage';

const TASK_ROLES: Array<'SUPER_ADMIN' | 'SALON_OWNER' | 'MANAGER' | 'STAFF' | 'ACCOUNTANT' | 'ANALYST' | 'MARKETER' | 'IT'> = [
  'SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT',
];

const ActiveSalonRouteRedirect: React.FC<{ segment: 'products' | 'audits' }> = ({ segment }) => {
  const { activeSalonId, salons, loading } = useSalonContextStore();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const resolvedSalonId = activeSalonId || salons[0]?.id;

  if (!resolvedSalonId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to={`/salon/${resolvedSalonId}/${segment}`} replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/s/:slug" element={<PublicSalonPage />} />
          <Route path="/s/:slug/rezerwacja" element={<PublicBookingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite/:token" element={<InviteAcceptPage />} />

          {/* Authenticated routes with sidebar layout */}
          <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Salon browsing (all roles) */}
            <Route path="/salons" element={<SalonsPage />} />
            <Route path="/salons/new" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER']}><CreateSalonPage /></ProtectedRoute>} />
            <Route path="/salons/:id" element={<SalonDetailPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/bookings/my" element={<MyBookingsPage />} />

            {/* Super Admin */}
            <Route path="/admin/salons" element={<ProtectedRoute roles={['SUPER_ADMIN']}><SuperAdminSalonsPage /></ProtectedRoute>} />
            <Route path="/admin/stats" element={<ProtectedRoute roles={['SUPER_ADMIN']}><SuperAdminStatsPage /></ProtectedRoute>} />

            {/* Salon Owner / Staff */}
            <Route path="/salon/settings" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF', 'IT']}><SalonSettingsPage /></ProtectedRoute>} />
            <Route path="/salon/services" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF']}><SalonServicesPage /></ProtectedRoute>} />
            {/* Kartoteka jest teraz jedyna lista zespolu — takze dla kierownictwa
                i ksiegowosci. Edycja zostaje po stronie wlasciciela: przyciski
                sa dla pozostalych rol ukryte, a API i tak ich nie dopuszcza. */}
            <Route path="/salon/staff" element={<ProtectedRoute roles={['SALON_OWNER', 'SUPER_ADMIN', 'MANAGER', 'ACCOUNTANT']}><SalonStaffPage /></ProtectedRoute>} />
            {/* Profil pracownika — jedno miejsce na wszystko o jednej osobie
                (uwaga klienta 4, krok 2). Dostepny takze kierownictwu
                i ksiegowosci, bo zawiera dane, po ktore chodzily one dotad
                na osobna strone „Wyniki pracownikow". */}
            <Route path="/salon/staff/:id" element={<ProtectedRoute roles={['SALON_OWNER', 'SUPER_ADMIN', 'MANAGER', 'ACCOUNTANT']}><StaffProfilePage /></ProtectedRoute>} />
            <Route path="/salon/calendar" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF']}><SalonCalendarPage /></ProtectedRoute>} />
            <Route path="/salon/clients" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF', 'MARKETER']}><SalonClientsPage /></ProtectedRoute>} />
            <Route path="/salon/inventory" element={<ProtectedRoute roles={['SALON_OWNER']}><SalonInventoryPage /></ProtectedRoute>} />
            <Route path="/salon/:salonId/inventory" element={<ProtectedRoute roles={['SALON_OWNER']}><SalonInventoryPage /></ProtectedRoute>} />
            <Route path="/salon/products" element={<ProtectedRoute roles={['SALON_OWNER']}><ActiveSalonRouteRedirect segment="products" /></ProtectedRoute>} />
            <Route path="/salon/:salonId/products" element={<ProtectedRoute roles={['SALON_OWNER']}><ProductsPage /></ProtectedRoute>} />
            <Route path="/salon/audits" element={<ProtectedRoute roles={['SALON_OWNER']}><ActiveSalonRouteRedirect segment="audits" /></ProtectedRoute>} />
            <Route path="/salon/:salonId/audits" element={<ProtectedRoute roles={['SALON_OWNER']}><InventoryAuditPage /></ProtectedRoute>} />
            <Route path="/salon/brands" element={<ProtectedRoute roles={['SALON_OWNER']}><BrandsPage /></ProtectedRoute>} />
            <Route path="/salon/brands/:brandId/warehouse" element={<ProtectedRoute roles={['SALON_OWNER']}><StockTransfersPage /></ProtectedRoute>} />
            <Route path="/salon/brands/:brandId/transfers" element={<ProtectedRoute roles={['SALON_OWNER']}><StockTransfersPage /></ProtectedRoute>} />
            <Route path="/salon/stats" element={<ProtectedRoute roles={['SALON_OWNER']}><SalonStatsPage /></ProtectedRoute>} />
            <Route path="/salon/sales" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF']}><SalonSalesPage /></ProtectedRoute>} />
            <Route path="/salon/marketing" element={<ProtectedRoute roles={['SALON_OWNER', 'MARKETER']}><SalonMarketingPage /></ProtectedRoute>} />
            <Route path="/salon/gifts" element={<ProtectedRoute roles={['SALON_OWNER', 'MARKETER']}><SalonGiftsPage /></ProtectedRoute>} />
            <Route path="/salon/profile" element={<ProtectedRoute roles={['SALON_OWNER', 'IT']}><SalonProfilePage /></ProtectedRoute>} />
            <Route path="/salon/website" element={<ProtectedRoute roles={['SALON_OWNER', 'IT']}><SalonWebsitePage /></ProtectedRoute>} />
            <Route path="/salon/account" element={<ProtectedRoute roles={['SALON_OWNER', 'STAFF', 'MANAGER', 'ACCOUNTANT', 'ANALYST', 'MARKETER', 'IT']}><SalonAccountPage /></ProtectedRoute>} />
            <Route path="/salon/users" element={<ProtectedRoute roles={['SALON_OWNER', 'SUPER_ADMIN']}><SalonUsersPage /></ProtectedRoute>} />
            <Route path="/salon/subscription" element={<ProtectedRoute roles={['SALON_OWNER']}><SalonSubscriptionPage /></ProtectedRoute>} />
            <Route path="/salon/payments" element={<ProtectedRoute roles={['SALON_OWNER']}><SalonPaymentsPage /></ProtectedRoute>} />

            {/* Employee module — self-service (mobile-first) */}
            <Route path="/me/time" element={<ProtectedRoute roles={['STAFF', 'MANAGER', 'SALON_OWNER']}><EmployeeTimePanel /></ProtectedRoute>} />
            <Route path="/me/absences" element={<ProtectedRoute roles={['STAFF', 'MANAGER', 'SALON_OWNER']}><MyAbsencesPage /></ProtectedRoute>} />
            <Route path="/me/payroll" element={<ProtectedRoute roles={['STAFF', 'MANAGER', 'SALON_OWNER']}><MyPayrollPage /></ProtectedRoute>} />

            {/* Employee module — Czas pracy */}
            <Route path="/time/dashboard" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><TimeDashboardPage /></ProtectedRoute>} />
            <Route path="/time/calendar" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><TimeCalendarPage /></ProtectedRoute>} />
            <Route path="/time/today" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><TodayAttendancePage /></ProtectedRoute>} />
            <Route path="/time/adjustments" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><AdjustmentsPage /></ProtectedRoute>} />
            <Route path="/time/absences" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><AbsencesAdminPage /></ProtectedRoute>} />
            <Route path="/time/settings" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER']}><SettlementSettingsPage /></ProtectedRoute>} />

            {/* Employee module — Wynagrodzenia */}
            <Route path="/payroll/periods" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ACCOUNTANT']}><PayrollPeriodsPage /></ProtectedRoute>} />
            <Route path="/payroll/simulator" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER']}><SimulatorPage /></ProtectedRoute>} />
            <Route path="/payroll/rules" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER']}><PayrollRulesPage /></ProtectedRoute>} />
            <Route path="/employees" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ACCOUNTANT']}><EmployeesPage /></ProtectedRoute>} />

            {/* Zadania */}
            <Route path="/tasks" element={<ProtectedRoute roles={TASK_ROLES}><TasksPage /></ProtectedRoute>} />
            <Route path="/tasks/stats" element={<ProtectedRoute roles={TASK_ROLES}><TaskStatsPage /></ProtectedRoute>} />
            <Route path="/tasks/:id" element={<ProtectedRoute roles={TASK_ROLES}><TaskDetailPage /></ProtectedRoute>} />

            {/* Employee module — Rentowność */}
            <Route path="/profitability" element={<ProtectedRoute roles={['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'ANALYST']}><ProfitabilityPage /></ProtectedRoute>} />

            {/* Client */}
            <Route path="/profile" element={<ProtectedRoute roles={['CLIENT']}><ClientProfilePage /></ProtectedRoute>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ConfirmProvider>
    </ThemeProvider>
  );
};

export default App;
